import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  HeartPulse,
  Lightbulb,
  Globe2,
  Code2,
  Stethoscope,
  Palette,
  BriefcaseBusiness,
  ArrowRight,
  Sparkles,
  Building2,
  Rocket,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";

/* =========================================================
   CUSTOM ILLUSTRATIONS
   These replace the simple Lucide icons in the product cards.
========================================================= */

const HealthcareIllustration = () => (
  <svg
    viewBox="0 0 100 100"
    className="w-[58px] h-[58px]"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="50" cy="50" r="39" fill="#FFF1B8" />

    <path
      d="M50 72C50 72 25 57 25 39C25 29 36 25 43 32L50 39L57 32C64 25 75 29 75 39C75 57 50 72 50 72Z"
      fill="#FFCB2E"
    />

    <path
      d="M50 42V58M42 50H58"
      stroke="#0F172A"
      strokeWidth="3.5"
      strokeLinecap="round"
    />

    <path
      d="M29 31L25 25M35 27L33 20M71 31L75 25M65 27L67 20"
      stroke="#FF8A3D"
      strokeWidth="2"
      strokeLinecap="round"
    />

    <path
      d="M25 63C31 65 35 69 38 74"
      stroke="#0F172A"
      strokeWidth="2.5"
      strokeLinecap="round"
    />

    <path
      d="M75 63C69 65 65 69 62 74"
      stroke="#0F172A"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const EngineeringIllustration = () => (
  <svg
    viewBox="0 0 100 100"
    className="w-[58px] h-[58px]"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="50" cy="50" r="39" fill="#FFF1B8" />

    <circle cx="50" cy="50" r="25" fill="#FFCB2E" />

    <path
      d="M39 39L31 47L39 55"
      stroke="#0F172A"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M61 39L69 47L61 55"
      stroke="#0F172A"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M55 34L45 60"
      stroke="#0F172A"
      strokeWidth="4"
      strokeLinecap="round"
    />

    <circle cx="29" cy="47" r="4" fill="#FF8A3D" />
    <circle cx="71" cy="47" r="4" fill="#FF8A3D" />

    <path
      d="M50 25V20M50 80V75M25 50H20M80 50H75"
      stroke="#FF8A3D"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const PeopleIllustration = () => (
   <svg
    viewBox="0 0 100 100"
    className="w-[58px] h-[58px]"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="50" cy="50" r="39" fill="#FFF1B8" />

    <circle cx="38" cy="41" r="10" fill="#FFCB2E" />
    <circle cx="62" cy="41" r="10" fill="#FFB74D" />

    <path
      d="M22 68C22 58 29 53 38 53C47 53 54 58 54 68"
      fill="#FFCB2E"
    />

    <path
      d="M46 68C46 58 53 53 62 53C71 53 78 58 78 68"
      fill="#FFB74D"
    />

    <path
      d="M38 45C34 45 31 42 31 38"
      stroke="#0F172A"
      strokeWidth="2"
      strokeLinecap="round"
    />

    <path
      d="M62 45C66 45 69 42 69 38"
      stroke="#0F172A"
      strokeWidth="2"
      strokeLinecap="round"
    />

    <path
      d="M30 75H70"
      stroke="#0F172A"
      strokeWidth="3"
      strokeLinecap="round"
    />

    <path
      d="M50 29V20M45 24H55"
      stroke="#FF8A3D"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const InnovationIllustration = () => (
   <svg
    viewBox="0 0 100 100"
    className="w-[58px] h-[58px]"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="50" cy="50" r="39" fill="#FFF1B8" />

    <path
      d="M50 25C39 25 32 33 32 43C32 50 36 54 41 59C44 62 45 66 45 70H55C55 66 56 62 59 59C64 54 68 50 68 43C68 33 61 25 50 25Z"
      fill="#FFCB2E"
    />

    <path
      d="M43 75H57"
      stroke="#0F172A"
      strokeWidth="3"
      strokeLinecap="round"
    />

    <path
      d="M45 81H55"
      stroke="#0F172A"
      strokeWidth="3"
      strokeLinecap="round"
    />

    <path
      d="M50 15V9M25 25L20 20M75 25L80 20M18 45H11M82 45H89"
      stroke="#FF8A3D"
      strokeWidth="2.5"
      strokeLinecap="round"
    />

    <path
      d="M45 43L49 47L57 37"
      stroke="#0F172A"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


const Careers = () => {

  const values = [
    {
      icon: HeartPulse,
      title: "Build With Purpose",
      description:
        "Create technology that solves meaningful problems and improves real-world experiences.",
    },
    {
      icon: Lightbulb,
      title: "Keep Learning",
      description:
        "Stay curious, experiment with new ideas and continuously improve your craft.",
    },
    {
      icon: Users,
      title: "Build Together",
      description:
        "Great products come from people who communicate openly, collaborate and learn from each other.",
    },
    {
      icon: Globe2,
      title: "Think Beyond",
      description:
        "Look at problems from different perspectives and build solutions that can make a wider impact.",
    },
  ];


  const teams = [
    {
      icon: Code2,
      title: "Engineering",
      description:
        "Build reliable software, platforms and digital products across the Cytozon ecosystem.",
    },
    {
      icon: Stethoscope,
      title: "Healthcare & Product",
      description:
        "Translate real healthcare needs into useful digital experiences, including Doxzon.",
    },
    {
      icon: Palette,
      title: "Design",
      description:
        "Create intuitive, accessible and meaningful experiences across our products.",
    },
    {
      icon: BriefcaseBusiness,
      title: "Business & Operations",
      description:
        "Help our products grow, operate effectively and create lasting value for customers.",
    },
  ];


  /* =========================================================
     CHANGED:
     Instead of Lucide icons, these now use custom illustrations.
  ========================================================= */

  const productAreas = [
    {
      illustration: HealthcareIllustration,
      title: "Healthcare",
      description:
        "Build technology that helps create better healthcare experiences.",
    },
    {
      illustration: EngineeringIllustration,
      title: "Engineering",
      description:
        "Build reliable and scalable digital products and platforms.",
    },
    {
      illustration: PeopleIllustration,
      title: "People",
      description:
        "Work with a collaborative team that values different perspectives.",
    },
    {
      illustration: InnovationIllustration,
      title: "Innovation",
      description:
        "Explore new ideas and turn them into useful digital experiences.",
    },
  ];


  const jobs = [
    {
      title: "React Native Developer",
      team: "Engineering",
      type: "Full-time",
    },
    {
      title: "Frontend Developer",
      team: "Engineering",
      type: "Full-time",
    },
    {
      title: "UI/UX Designer",
      team: "Design",
      type: "Full-time",
    },
    {
      title: "Healthcare Product Specialist",
      team: "Healthcare & Product",
      type: "Full-time",
    },
  ];


  const hiringSteps = [
    {
      number: "01",
      title: "Apply",
      description:
        "Find a role that matches your skills and share your profile with us.",
    },
    {
      number: "02",
      title: "Connect",
      description:
        "Have an initial conversation and learn more about the opportunity.",
    },
    {
      number: "03",
      title: "Explore",
      description:
        "Discuss your experience, approach and how you solve problems.",
    },
    {
      number: "04",
      title: "Join",
      description:
        "If there is a mutual fit, we'll welcome you to the team.",
    },
  ];


  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}

        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute -top-40 -right-40 w-[650px] h-[650px] rounded-full bg-teal-200/25 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-200/25 blur-3xl" />


          <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-2 gap-16 items-center">

            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Careers at Cytozon
                </span>

              </div>


              <h1 className="font-sora text-5xl md:text-6xl lg:text-[64px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Build technology

                <span className="block text-teal-500 mt-2">
                  that matters.
                </span>

              </h1>


              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Join Cytozon Technologies and help build digital products
                that solve meaningful problems. Doxzon is our
                healthcare-focused platform, bringing technology and
                connected healthcare experiences together.
              </p>


              <div className="flex flex-wrap gap-3 mt-9">

                <a
                  href="#open-positions"
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-12 text-sm font-semibold hover:bg-slate-800 transition"
                >
                  Explore Open Positions
                  <ArrowRight size={16} />
                </a>


                <a
                  href="#why-cytozon"
                  className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 text-slate-800 px-6 h-12 text-sm font-semibold hover:border-teal-200 hover:bg-teal-50 transition"
                >
                  Why Cytozon?
                </a>

              </div>


              <div className="flex flex-wrap gap-6 mt-8 text-sm text-slate-500">

                <span className="inline-flex items-center gap-2">
                  <HeartPulse
                    size={16}
                    className="text-teal-500"
                  />
                  Purpose-driven products
                </span>

                <span className="inline-flex items-center gap-2">
                  <Sparkles
                    size={16}
                    className="text-teal-500"
                  />
                  Continuous learning
                </span>

              </div>

            </motion.div>


            {/* HERO VISUAL */}

            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />


              <div className="relative rounded-[32px] bg-white border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-7">

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Cytozon Technologies
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Build. Learn. Create.
                    </h3>

                  </div>


                  <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">

                    <Building2
                      size={23}
                      className="text-teal-600"
                    />

                  </div>

                </div>


                <div className="mt-7 rounded-2xl bg-slate-950 p-6">

                  <div className="flex items-center gap-3">

                    <div className="w-11 h-11 rounded-xl bg-teal-500/15 grid place-items-center">

                      <HeartPulse
                        size={21}
                        className="text-teal-400"
                      />

                    </div>


                    <div>

                      <p className="text-white font-semibold">
                        Doxzon
                      </p>

                      <p className="text-slate-400 text-xs mt-1">
                        Healthcare technology platform
                      </p>

                    </div>

                  </div>


                  <p className="text-white font-semibold mt-6">
                    Technology with a purpose.
                  </p>

                  <p className="text-slate-400 text-sm leading-6 mt-2">
                    Build products that connect people, services and
                    experiences around meaningful problems.
                  </p>

                </div>


                <div className="grid grid-cols-2 gap-3 mt-4">
  {[
    [Code2, "Engineering"],
    [Palette, "Design"],
    [Stethoscope, "Healthcare"],
    [BriefcaseBusiness, "Operations"],
  ].map(([Icon, title]) => (
    <div
      key={title}
      className="rounded-2xl bg-slate-50 border border-slate-200 p-4 shadow-sm hover:bg-white hover:border-teal-200 hover:shadow-md transition"
    >
      <div className="w-9 h-9 rounded-xl bg-slate-100 grid place-items-center">
        <Icon
          size={17}
          className="text-slate-700"
        />
      </div>

      <p className="text-xs font-semibold text-slate-900 mt-3">
        {title}
      </p>
    </div>
  ))}
</div>

              </div>

            </motion.div>

          </div>

        </section>


        {/* =====================================================
            WHY CYTOZON
        ====================================================== */}

        <section
          id="why-cytozon"
          className="py-24 md:py-28"
        >

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-3xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Why Cytozon
              </span>


              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Work on products

                <span className="block text-teal-500">
                  that have a purpose.
                </span>

              </h2>


              <p className="text-slate-600 leading-8 mt-5">
                At Cytozon Technologies, we bring together technology,
                creativity and problem-solving to build digital products
                that address real needs.
              </p>


              <p className="text-slate-600 leading-8 mt-4">
                From healthcare through Doxzon to other digital products
                and solutions, our work is driven by the people who use
                what we build.
              </p>

            </div>


            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-10">

              {values.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.06 }}
                    whileHover={{ y: -4 }}
                    className="rounded-xl border border-slate-100 bg-slate-50/70 p-4 hover:bg-white hover:shadow-md hover:border-teal-100 transition"
                  >

                    <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center">

                      <Icon
                        size={17}
                        className="text-teal-600"
                      />

                    </div>


                    <h3 className="font-sora text-sm font-semibold mt-3">
                      {item.title}
                    </h3>


                    <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>


        {/* =====================================================
            WHAT WE BUILD
            CUSTOM ILLUSTRATED CARDS
        ====================================================== */}

        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-7xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                What We Build
              </span>


              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Different products.

                <span className="text-teal-500">
                  {" "}One engineering mindset.
                </span>

              </h2>


              <p className="text-slate-600 leading-7 mt-5">
                Work with people across disciplines to turn ideas,
                requirements and real-world problems into useful digital
                products.
              </p>

            </div>


            {/* =================================================
                ILLUSTRATED CARDS
            ================================================== */}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-10">

  {productAreas.map((item, index) => {
    const Illustration = item.illustration;

    return (
      <motion.div
        key={item.title}
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        viewport={{ once: true }}
        transition={{
          duration: 0.5,
          delay: index * 0.06,
        }}
        whileHover={{ y: -4 }}
        className="group rounded-2xl border border-teal-100 bg-[#f0fafa] p-3 md:p-4 text-center hover:bg-white hover:shadow-lg transition"
      >

        {/* Illustration */}
        <div className="flex justify-center">
          <div className="w-[64px] h-[64px] rounded-full bg-white flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-300">
            <Illustration />
          </div>
        </div>

        {/* Title */}
        <h3 className="font-sora text-sm md:text-base font-semibold mt-3 text-slate-900">
          {item.title}
        </h3>

        {/* Description */}
        <p className="text-slate-600 text-[11px] md:text-xs leading-5 mt-1.5">
          {item.description}
        </p>

        {/* Bottom accent */}
        <div className="mx-auto mt-3 w-6 h-1 rounded-full bg-[#ffcb2e] group-hover:w-8 transition-all duration-300" />

      </motion.div>
    );
  })}

</div>

          </div>

        </section>


        {/* =====================================================
            BUILDING DOXZON
        ====================================================== */}

        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="grid lg:grid-cols-[1fr_1fr] gap-14 items-center">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Building Doxzon
                </span>


                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  Technology that connects

                  <span className="block text-teal-500">
                    healthcare experiences.
                  </span>

                </h2>


                <p className="text-slate-600 leading-8 mt-6">
                  Doxzon is Cytozon's healthcare-focused digital platform.
                  It brings together patients, healthcare professionals
                  and healthcare workflows through connected experiences.
                </p>


                <p className="text-slate-600 leading-8 mt-4">
                  Working on Doxzon means solving interesting problems
                  across technology, product, healthcare workflows and
                  user experience.
                </p>


                <a
                  href="https://www.doxzon.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-11 text-sm font-semibold hover:bg-teal-500 transition"
                >
                  Explore Doxzon
                  <ArrowRight size={15} />
                </a>

              </div>


              <div className="grid grid-cols-2 gap-3">

                {[
                  {
                    icon: HeartPulse,
                    title: "Healthcare",
                  },
                  {
                    icon: Code2,
                    title: "Engineering",
                  },
                  {
                    icon: Users,
                    title: "People",
                  },
                  {
                    icon: Sparkles,
                    title: "Innovation",
                  },
                ].map((item) => {

                  const Icon = item.icon;

                  return (
                    <motion.div
                      key={item.title}
                      whileHover={{
                        y: -5,
                      }}
                      className="rounded-2xl border border-slate-100 bg-slate-50 p-6"
                    >

                      <div className="w-10 h-10 rounded-xl bg-white grid place-items-center">

                        <Icon
                          size={18}
                          className="text-teal-600"
                        />

                      </div>


                      <h3 className="font-sora text-sm font-semibold mt-4">
                        {item.title}
                      </h3>

                    </motion.div>
                  );

                })}

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            TEAMS
        ====================================================== */}

        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-7xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Where You Can Contribute
              </span>


              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Find your place

                <span className="text-teal-500">
                  {" "}at Cytozon.
                </span>

              </h2>


              <p className="text-slate-600 leading-7 mt-5">
                Different disciplines, different perspectives and one
                shared goal: build products that people can rely on.
              </p>

            </div>


            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mt-12">

              {teams.map((team, index) => {

                const Icon = team.icon;

                return (
                  <motion.div
                    key={team.title}
                    initial={{
                      opacity: 0,
                      y: 20,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      delay: index * 0.06,
                    }}
                    whileHover={{
                      y: -4,
                    }}
                    className="rounded-xl bg-white border border-slate-100 p-4 shadow-sm hover:shadow-md hover:border-teal-100 transition"
                  >

                    <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center">

                      <Icon
                        size={17}
                        className="text-teal-600"
                      />

                    </div>


                    <h3 className="font-sora text-sm font-semibold mt-3">
                      {team.title}
                    </h3>


                    <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
                      {team.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>


        {/* =====================================================
            OPEN POSITIONS
        ====================================================== */}

        <section
          id="open-positions"
          className="py-24 md:py-28"
        >

          <div className="max-w-6xl mx-auto px-6">

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Open Positions
                </span>


                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  Find your next

                  <span className="text-teal-500">
                    {" "}challenge.
                  </span>

                </h2>


                <p className="text-slate-600 mt-5">
                  Explore roles where you can contribute your skills and
                  grow with the team.
                </p>

              </div>


              <a
                href="mailto:careers@cytozon.com"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 h-11 text-sm font-semibold hover:border-teal-200 hover:text-teal-600 transition"
              >
                Contact Careers
                <ArrowRight size={15} />
              </a>

            </div>


            <div className="space-y-3 mt-12">

              {jobs.map((job, index) => (

                <motion.div
                  key={job.title}
                  initial={{
                    opacity: 0,
                    y: 15,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    delay: index * 0.06,
                  }}
                  whileHover={{
                    x: 4,
                  }}
                  className="group rounded-2xl border border-slate-100 bg-white p-5 md:p-6 shadow-sm hover:shadow-lg hover:border-teal-100 transition"
                >

                  <div className="flex flex-col lg:flex-row lg:items-center gap-5">

                    <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center shrink-0">

                      <BriefcaseBusiness
                        size={19}
                        className="text-teal-600"
                      />

                    </div>


                    <div className="flex-1">

                      <h3 className="font-sora font-semibold text-base">
                        {job.title}
                      </h3>


                      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-2 text-xs text-slate-400">

                        <span>
                          {job.team}
                        </span>

                        <span>
                          {job.type}
                        </span>

                      </div>

                    </div>


                    <a
                      href="mailto:careers@cytozon.com"
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 text-white px-5 h-11 text-sm font-semibold group-hover:bg-teal-500 transition"
                    >
                      Enquire
                      <ArrowRight size={15} />
                    </a>

                  </div>

                </motion.div>

              ))}

            </div>


            <p className="text-xs text-slate-400 mt-6">
              Roles shown here should be replaced with your confirmed
              vacancies before publishing the careers page.
            </p>

          </div>

        </section>


        {/* =====================================================
            HIRING PROCESS
        ====================================================== */}

        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Our Hiring Process
              </span>


              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Simple, transparent,

                <span className="block text-teal-400">
                  people-first.
                </span>

              </h2>


              <p className="text-slate-400 leading-7 mt-5">
                We want candidates to understand what to expect and have
                meaningful conversations throughout the process.
              </p>

            </div>


            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-white/10" />


              {hiringSteps.map((step) => (

                <motion.div
                  key={step.number}
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  className="relative text-center"
                >

                  <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-teal-500 border-4 border-slate-950 shadow-lg grid place-items-center">

                    <span className="text-white text-xs font-bold">
                      {step.number}
                    </span>

                  </div>


                  <h3 className="font-sora text-lg font-semibold mt-6">
                    {step.title}
                  </h3>


                  <p className="text-slate-400 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                    {step.description}
                  </p>

                </motion.div>

              ))}

            </div>

          </div>

        </section>


        {/* =====================================================
            CTA
        ====================================================== */}

        <section className="px-6 py-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-[#f4fbfc] border border-teal-50 px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-200/30 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-200/30 blur-3xl" />


            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Join Cytozon
              </span>


              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Ready to build

                <span className="block text-teal-500">
                  something meaningful?
                </span>

              </h2>


              <p className="text-slate-600 max-w-2xl mx-auto mt-5 leading-7">
                If you're excited about technology, solving meaningful
                problems and building products that people use, we'd love
                to hear from you.
              </p>


              <a
                href="#open-positions"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-950 text-white px-7 py-4 font-semibold hover:bg-teal-500 transition"
              >
                Explore Open Positions
                <ArrowRight size={17} />
              </a>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default Careers;